import { useEffect, useRef, useState } from "preact/hooks";
import { defaultModeName } from "../../api/modes";
import { GameMode } from "../../api/types";
import { requireMe } from "../../state/auth";
import { weaponName } from "../../state/meta";
import { navigate } from "../../state/route";
import type { Screen } from "../../state/route";
import { Badge } from "../components/Badge";
import type { BadgeTone } from "../components/Badge";
import { Button } from "../components/Button";
import { Progress } from "../components/Progress";
import { Visualizer } from "../components/Visualizer";
import { formatInt, formatRatio } from "../format";
import { useArrowNav } from "../hooks/useArrowNav";
import { AccountModal } from "./AccountModal";

interface MenuEntry {
  screen: Screen;
  label: string;
  hint: string;
  primary?: boolean;
}

const ENTRIES: readonly MenuEntry[] = [
  { screen: "play", label: "Play", hint: "Quick match in any mode", primary: true },
  { screen: "ranked", label: "Ranked", hint: `Rated ${defaultModeName(GameMode.DEFUSAL)} and ${defaultModeName(GameMode.TDM)}` },
  { screen: "browser", label: "Server browser", hint: "Join a public or custom room" },
  { screen: "create", label: "Create lobby", hint: "Host your own match" },
  { screen: "loadouts", label: "Loadouts", hint: "Weapons, attachments, perks" },
  { screen: "profile", label: "Profile", hint: "Stats, weapon levels, unlocks" },
  { screen: "settings", label: "Settings", hint: "Controls, video, audio" },
];

const TIER_TONES: Readonly<Record<string, BadgeTone>> = {
  Iron: "neutral",
  Bronze: "neutral",
  Silver: "ink",
  Gold: "gold",
  Platinum: "teal",
  Diamond: "teal",
  Apex: "red",
};

function PlayerCard({ onAccount }: { onAccount: () => void }) {
  const me = requireMe();
  const p = me.profile;
  const loadout = me.loadouts[me.selectedLoadout];
  return (
    <aside class="playercard" aria-label="Player">
      <div class="playercard__body">
        <div class="playercard__id well">
          <div class="playercard__top">
            <div class="playercard__name-block">
              <span class="eyebrow">{me.user.isGuest ? "Guest" : `@${me.user.username}`}</span>
              <h2 class="playercard__name" title={me.user.displayName}>
                {me.user.displayName}
              </h2>
            </div>
            <div class="playercard__level" aria-label={`Level ${p.level}`}>
              <span class="eyebrow">Lvl</span>
              <span class="playercard__lvl lcd num" data-ghost="88">
                {p.level}
              </span>
            </div>
          </div>
          <div class="playercard__xp">
            <Progress value={p.xpIntoLevel} max={p.xpForNext} label="Experience to next level" />
            <span class="playercard__xp-text num">
              {p.xpForNext > 0 ? `${formatInt(p.xpIntoLevel)} / ${formatInt(p.xpForNext)} XP` : "Max level"}
            </span>
          </div>
        </div>
        <div class="playercard__rank well">
          <Badge tone={TIER_TONES[p.rankTier] ?? "neutral"}>{p.rankTier}</Badge>
          <span class="playercard__rating num">{formatInt(p.rating)} rating</span>
        </div>
        <dl class="playercard__stats">
          <div class="well">
            <dt>Matches</dt>
            <dd class="num">{formatInt(p.matches)}</dd>
          </div>
          <div class="well">
            <dt>Wins</dt>
            <dd class="num">{formatInt(p.wins)}</dd>
          </div>
          <div class="well">
            <dt>K/D</dt>
            <dd class="num">{formatRatio(p.kills, p.deaths)}</dd>
          </div>
        </dl>
        <div class="playercard__loadout well">
          <span class="eyebrow">Active loadout</span>
          <span class="playercard__loadout-name">{loadout?.name ?? "-"}</span>
          {loadout && (
            <span class="playercard__loadout-guns muted">
              {weaponName(loadout.primary.weapon)} / {weaponName(loadout.secondary.weapon)}
            </span>
          )}
        </div>
        <Button size="sm" onClick={onAccount} full>
          {me.user.isGuest ? "Register or log in" : "Account"}
        </Button>
      </div>
    </aside>
  );
}

export function MainMenu() {
  const nav = useRef<HTMLElement>(null);
  const [accountOpen, setAccountOpen] = useState(false);
  useArrowNav(nav);
  // Keyboard users land on PLAY; returning from a sub screen restores that focus.
  useEffect(() => nav.current?.querySelector<HTMLElement>("[data-nav]")?.focus(), []);

  return (
    <div class="mainmenu screen-enter">
      <section class="mainmenu__panel">
        <header class="mainmenu__brand dplate">
          <h1 class="mainmenu__logo dplate__title">Gunrun</h1>
          <p class="mainmenu__tag">Parkour. Precision. Pressure.</p>
          <Visualizer class="dplate__viz" bars={14} />
        </header>
        <nav ref={nav} class="mainmenu__list" aria-label="Main menu">
          {ENTRIES.map((entry) => (
            <button
              key={entry.screen}
              type="button"
              class={entry.primary ? "menuitem menuitem--primary" : "menuitem"}
              onClick={() => navigate(entry.screen)}
              data-nav=""
            >
              <span class="menuitem__label">{entry.label}</span>
              <span class="menuitem__hint">{entry.hint}</span>
              <svg class="menuitem__arrow" viewBox="0 0 16 16" width="16" height="16" aria-hidden="true">
                <path d="M6 2l6 6-6 6" stroke="currentColor" stroke-width="2" fill="none" />
              </svg>
            </button>
          ))}
        </nav>
      </section>
      <PlayerCard onAccount={() => setAccountOpen(true)} />
      {accountOpen && <AccountModal onClose={() => setAccountOpen(false)} />}
    </div>
  );
}
